/* eslint-disable max-statements */
import { useEffect, useState } from 'react';

import type { NavRouteType } from '../types';
import { fetchCallAiUserData } from './callaiApi';

export type PermissionKey = string;

type PermissionTaskResult = unknown;
type PermissionResultMap = Record<PermissionKey, PermissionTaskResult>;

type PermissionTask = (sessionId: string, companyId: string) => Promise<PermissionTaskResult>;
type PermissionTaskMap = Record<PermissionKey, { operation: PermissionTask }>;

type PermissionVerifier = (results: PermissionResultMap) => boolean;
type PermissionVerifierMap = Record<string, PermissionVerifier>;

/**
 * Permission tasks configuration
 * - Keys correspond to values in `permissionsMap` coming from BE navigation config.
 */
export const PERMISSIONS_MAP_TASK: PermissionTaskMap = {
  CALL_AI_USER_DATA: {
    /**
     * Fetches CallAI user + company data.
     * This is the primary payload used by the verification functions below.
     */
    operation: async (sessionId, companyId) => fetchCallAiUserData(sessionId, companyId),
  },
};

/**
 * Derive a simple "feature enabled" lookup map from the CallAI feature flags payload.
 */
const buildFeatureEnablementConfig = (featureFlags: any[] | undefined | null) => {
  const config: Record<string, boolean> = {};

  if (!Array.isArray(featureFlags)) {
    return config;
  }

  featureFlags.forEach(item => {
    const { name, active, isEnabledForTenant } = item || {};
    if (!name) return;
    config[name] = Boolean(active && isEnabledForTenant);
  });

  return config;
};

const isFeatureEnabled = (config: Record<string, boolean>, featureName: string) => {
  const value = config[featureName];
  return Boolean(value);
};

/**
 * Verification configuration
 * - Keys correspond to submenu ids (e.g. CALL_AI_TEAM_RECORDINGS).
 * - Each verifier receives the aggregated permission results and returns a boolean
 *   indicating whether the submenu item should be visible.
 */
export const PERMISSIONS_MAP_VERIFICATION: PermissionVerifierMap = {
  CALL_AI_TEAM_RECORDINGS: (results: PermissionResultMap) => {
    const userData: any = results.CALL_AI_USER_DATA;
    if (userData?.errors) return false;
    return Boolean(userData?.data?.auth?.user?.isManager);
  },

  CALL_AI_UPCOMING_MEETINGS: (results: PermissionResultMap) => {
    const userData: any = results.CALL_AI_USER_DATA;
    if (userData?.errors) return false;
    const featureFlags = userData?.companySettings?.getFeatureFlags;
    const featureConfig = buildFeatureEnablementConfig(featureFlags);

    const showWorkflowBannersEnabled = isFeatureEnabled(featureConfig, 'show_workflow_banners');
    const userRoles: string[] = userData?.data?.auth?.user?.roles ?? [];
    const isRecorder = userRoles.includes('recorder');

    // Hide menu when workflow banners are enabled but user is not a recorder.
    return !(showWorkflowBannersEnabled && !isRecorder);
  },

  CALL_AI_MY_ANALYTICS: (results: PermissionResultMap) => {
    const userData: any = results.CALL_AI_USER_DATA;
    if (userData?.errors) return false;
    const user = userData?.data?.auth?.user;
    const roles: string[] = user?.roles ?? [];

    const isRecorder = roles.includes('recorder');
    const isManagerOrAdmin = Boolean(user?.isManager || user?.isAdmin);
    return isRecorder || !isManagerOrAdmin;
  },
};

type UsePermissionsCheckArgs = {
  permissionsMap: PermissionKey[];
  subMenu: NavRouteType[];
  sessionId: string;
  companyId: string;
};

type UsePermissionsCheckReturn = {
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
};

/**
 * Runs configured permission tasks and computes visibility for each submenu item.
 */
export const usePermissionsCheck = ({
  permissionsMap,
  subMenu,
  sessionId,
  companyId,
}: UsePermissionsCheckArgs): UsePermissionsCheckReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubMenuVisible, setIsSubMenuVisible] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const hasPermissionConfig = Array.isArray(permissionsMap) && permissionsMap.length > 0;
    const hasGatedSubMenu = subMenu.some(item => item.showAfterPermissionsCheck);

    if (!hasPermissionConfig || !hasGatedSubMenu) {
      setIsLoading(false);
      setIsSubMenuVisible({});
      return;
    }

    let isMounted = true;

    const buildVisibilityMap = (results: PermissionResultMap | null): Record<string, boolean> => {
      const visibility: Record<string, boolean> = {};

      subMenu.forEach(({ id, showAfterPermissionsCheck }) => {
        if (!id) return;

        // Non-gated submenu items are always visible
        if (!showAfterPermissionsCheck) {
          visibility[id] = true;
          return;
        }

        // If tasks failed, hide gated items by default
        if (!results) {
          visibility[id] = false;
          return;
        }

        const verifier = PERMISSIONS_MAP_VERIFICATION[id];
        visibility[id] = verifier ? verifier(results) : false;
      });

      return visibility;
    };

    const runPermissionTasks = async () => {
      setIsLoading(true);

      const safeSetVisibility = (results: PermissionResultMap | null) => {
        if (!isMounted) return;
        setIsSubMenuVisible(buildVisibilityMap(results));
      };

      try {
        const validPermissionKeys = permissionsMap.filter(key =>
          Boolean(PERMISSIONS_MAP_TASK[key])
        );

        if (validPermissionKeys.length === 0) {
          // No known tasks for provided keys → only non-gated items are visible
          safeSetVisibility(null);
          return;
        }

        const taskResults = await Promise.all(
          validPermissionKeys.map(async key => {
            const result = await PERMISSIONS_MAP_TASK[key].operation(sessionId, companyId);
            return [key, result] as const;
          })
        );

        if (!isMounted) return;

        const aggregatedResults: PermissionResultMap = {};
        taskResults.forEach(([key, result]) => {
          aggregatedResults[key] = result;
        });

        safeSetVisibility(aggregatedResults);
      } catch {
        // On error, show only non-permission-gated submenu items
        safeSetVisibility(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    runPermissionTasks();

    return () => {
      isMounted = false;
    };
  }, [permissionsMap, subMenu, sessionId, companyId]);

  return {
    isLoading,
    isSubMenuVisible,
  };
};

export default usePermissionsCheck;
